// @ts-check
// Shared sessions domain client.
//
// Owns the `/api/sessions/*` paths. Web and mobile SessionService are identical
// path maps; Guardian uses a matching subset (detail/confirm/cancel). Each app
// passes an adapter so its transport, caching, and cache-invalidation stay
// app-side. The adapter mirrors the createApiClient method surface:
//   get(path, options), post(path, body, options),
//   patch(path, body, options), delete(path, options)

import { joinPath, withQuery } from '../path-utils.js';
import { readSessionIdentifier, readSessionListQuery, readSessionWriteRequest } from '../session-requests.js';
export { readSessionListResponse, readSessionRecordResponse, readSessionConfirmationResponse, readSessionCancellationResponse, readSessionMessageResponse, readSessionApprovalResponse, SessionResponseContractError } from '../session-responses.js';
export { readSessionIdentifier, readSessionListQuery, readSessionWriteRequest, SessionRequestContractError } from '../session-requests.js';

/**
 * @typedef {object} SessionsAdapter
 * @property {(path: string, options?: unknown) => Promise<unknown>} get
 * @property {(path: string, body?: unknown, options?: unknown) => Promise<unknown>} post
 * @property {(path: string, body?: unknown, options?: unknown) => Promise<unknown>} patch
 * @property {(path: string, options?: unknown) => Promise<unknown>} delete
 */

export const SESSION_PATHS = {
  collection: '/api/sessions',
};

export const buildSessionPath = (id) =>
  joinPath(SESSION_PATHS.collection, id);

export const buildSessionCreatePath = (activityId) =>
  `${buildSessionPath(activityId)}/create`;

export const buildSessionActionPath = (id, action) =>
  `${buildSessionPath(id)}/${action}`;

export const buildSessionMemberActionPath = (id, action, memberId) =>
  joinPath(buildSessionActionPath(id, action), memberId);

export const buildSessionConfirmPath = (sessionId, { retry } = {}) => {
  const path = buildSessionActionPath(sessionId, 'confirm');
  return withQuery(path, { retry });
};

/**
 * @param {SessionsAdapter} adapter
 */
export const createSessionsClient = (adapter) => {
  if (!adapter || typeof adapter !== 'object') {
    throw new TypeError('createSessionsClient requires a transport adapter.');
  }

  const action = (id, name, options) =>
    adapter.post(buildSessionActionPath(readSessionIdentifier(id), name), null, options);

  const memberAction = (id, name, memberId, options) =>
    adapter.post(buildSessionMemberActionPath(readSessionIdentifier(id), name, readSessionIdentifier(memberId)), null, options);

  return {
    list(options = {}) {
      readSessionListQuery(options.params);
      return adapter.get(SESSION_PATHS.collection, options);
    },

    getById(sessionId, options = {}) {
      readSessionIdentifier(sessionId);
      return adapter.get(buildSessionPath(sessionId), options);
    },

    create(activityId, sessionData, options = {}) {
      readSessionIdentifier(activityId);
      return adapter.post(buildSessionCreatePath(activityId), readSessionWriteRequest(sessionData, 'session creation'), options);
    },

    update(sessionId, updates, options = {}) {
      readSessionIdentifier(sessionId);
      return adapter.patch(buildSessionPath(sessionId), readSessionWriteRequest(updates), options);
    },

    remove(sessionId, options = {}) {
      readSessionIdentifier(sessionId);
      return adapter.delete(buildSessionPath(sessionId), options);
    },

    cancel(sessionId, options = {}) {
      return action(sessionId, 'cancel', options);
    },

    confirm(sessionId, options = {}) {
      readSessionIdentifier(sessionId);
      return adapter.post(buildSessionActionPath(sessionId, 'confirm'), null, options);
    },

    requestParticipation(sessionId, options = {}) {
      return action(sessionId, 'request-participation', options);
    },

    cancelParticipationRequest(sessionId, userId, options = {}) {
      return memberAction(sessionId, 'cancel-request-participation', userId, options);
    },

    requestFacilitator(sessionId, options = {}) {
      return action(sessionId, 'request-facilitation', options);
    },

    cancelFacilitatorRequest(sessionId, userId, options = {}) {
      return memberAction(sessionId, 'cancel-request-facilitation', userId, options);
    },

    approveParticipant(sessionId, participantId, options = {}) {
      return memberAction(sessionId, 'approve-participant', participantId, options);
    },

    denyParticipant(sessionId, participantId, options = {}) {
      return memberAction(sessionId, 'deny-participant', participantId, options);
    },

    approveFacilitator(sessionId, facilitatorId, options = {}) {
      return memberAction(sessionId, 'approve-facilitator', facilitatorId, options);
    },

    denyFacilitator(sessionId, facilitatorId, options = {}) {
      return memberAction(sessionId, 'deny-facilitator', facilitatorId, options);
    },

    leaveAsParticipant(sessionId, userId, options = {}) {
      return memberAction(sessionId, 'leave-participant', userId, options);
    },

    leaveAsFacilitator(sessionId, userId, options = {}) {
      return memberAction(sessionId, 'leave-facilitator', userId, options);
    },
  };
};
